/**
 * Phase B — Sync Status page (/m/sync).
 *
 * Shows feature-typed pending entries ("2 DPRs, 1 Safety Observation"), a
 * manual "Sync now" action, and per-entry detail (queued time, attempts, last
 * error). Discardable entries can be removed.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw, CloudOff, CheckCircle2, Trash2, Wifi, AlertTriangle, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';
import { queueSummary, removeJob } from '../../lib/offlineQueue';
import { formatDate } from '../../lib/format';

export default function MobileSyncStatus() {
  const { online, pending, syncing, flush } = useNetworkStatus();
  const [summary, setSummary] = useState({ total: 0, byFeature: {}, jobs: [] });

  const load = useCallback(async () => {
    try { setSummary(await queueSummary()); } catch { /* idb unavailable */ }
  }, []);

  useEffect(() => { load(); }, [load, pending, syncing]);

  const onSyncNow = async () => {
    if (!online) { toast.error('Still offline — connect to a network first.'); return; }
    const res = await flush();
    if (res.synced > 0) toast.success(`Synced ${res.synced} entr${res.synced === 1 ? 'y' : 'ies'}`);
    if (res.failed > 0) toast.error(`${res.failed} entr${res.failed === 1 ? 'y' : 'ies'} failed — will retry`);
    if (res.synced === 0 && res.failed === 0) toast.info('Nothing to sync');
    load();
  };

  const onDiscard = async (id) => {
    await removeJob(id);
    toast.success('Entry discarded');
    load();
  };

  const featureEntries = Object.entries(summary.byFeature);

  return (
    <div className="p-4 space-y-4 max-w-lg mx-auto" data-testid="mobile-sync-status-page">
      <div>
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E]">Offline Sync</div>
        <h1 className="font-display font-bold text-2xl mt-0.5">Sync Status</h1>
      </div>

      {/* Connectivity banner */}
      <div
        className={`flex items-center gap-2 px-4 py-3 rounded-lg border text-sm font-display font-semibold ${
          online ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-amber-50 text-amber-800 border-amber-200'
        }`}
        data-testid="sync-connectivity-banner"
      >
        {online ? <Wifi size={16} /> : <CloudOff size={16} />}
        {online ? 'Online — connected to server' : 'Offline — entries are saved on this device'}
      </div>

      {/* Pending summary */}
      <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="sync-summary">
        <div className="flex items-center justify-between">
          <div>
            <div className="kpi-overline">Pending entries</div>
            <div className="font-display font-bold text-3xl tabular-nums mt-0.5" data-testid="sync-pending-count">{summary.total}</div>
          </div>
          <button
            type="button"
            onClick={onSyncNow}
            disabled={syncing || summary.total === 0 || !online}
            data-testid="sync-now-btn"
            className="flex items-center gap-2 px-4 py-2.5 bg-[#1F7A6E] text-white text-sm font-display font-semibold rounded-sm hover:bg-[#14504E] disabled:opacity-40 transition"
          >
            <RefreshCw size={15} className={syncing ? 'animate-spin' : ''} /> {syncing ? 'Syncing…' : 'Sync now'}
          </button>
        </div>

        {featureEntries.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid="sync-feature-breakdown">
            {featureEntries.map(([label, count]) => (
              <span key={label} className="inline-flex items-center gap-1 px-2.5 py-1 bg-teal-50 text-teal-800 border border-teal-200 rounded-full text-xs font-display font-semibold">
                {count} {label}{count > 1 ? 's' : ''}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Empty state */}
      {summary.total === 0 && (
        <div className="text-center py-12 bg-white border border-dashed border-slate-200 rounded-lg" data-testid="sync-empty">
          <CheckCircle2 size={28} className="mx-auto text-emerald-500 mb-2" />
          <div className="font-display font-semibold text-sm text-slate-700">All caught up</div>
          <div className="text-xs text-slate-400 mt-1">No entries waiting to sync.</div>
        </div>
      )}

      {/* Per-entry list */}
      <div className="space-y-2">
        {summary.jobs.map((job) => (
          <div key={job.id} className="bg-white border border-slate-200 rounded-lg p-3" data-testid={`sync-entry-${job.feature}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-display font-bold text-sm">{job.label}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Queued {formatDate(job.createdAt)} · {new Date(job.createdAt).toLocaleTimeString()}
                </div>
                <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-500">
                  {job.payload?.project_name && <span className="truncate">{job.payload.project_name}</span>}
                  {(job.photos?.length || 0) > 0 && (
                    <span className="inline-flex items-center gap-1"><ImageIcon size={11} /> {job.photos.length}</span>
                  )}
                  {job.attempts > 0 && <span className="text-amber-700">{job.attempts} attempt{job.attempts > 1 ? 's' : ''}</span>}
                </div>
                {job.lastError && (
                  <div className="flex items-center gap-1 mt-1 text-[11px] text-rose-700">
                    <AlertTriangle size={11} /> {job.lastError}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => onDiscard(job.id)}
                data-testid={`sync-discard-${job.id}`}
                className="text-slate-400 hover:text-rose-600 p-1 shrink-0"
                title="Discard this entry"
              >
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
